/**
 * Context Sync Service
 *
 * Provides methods to sync frontend context (screen, data, selections, filters, settings)
 * to the backend via the Context API. This enables agents to understand the current
 * frontend state without requiring context to be passed with each chat message.
 *
 * Usage:
 *   import { contextSyncService } from '@/services/contextSync';
 *
 *   // Set session ID when chat starts
 *   contextSyncService.setSessionId(sessionId);
 *
 *   // Sync context as user navigates/interacts
 *   contextSyncService.updateScreenContext({ screen_name: 'buying-table', screen_path: '/po/create' });
 *   contextSyncService.updateDataContext('reorder_suggestions', products);
 *   contextSyncService.updateSelectionContext('products', selectedIds);
 */

import debounce from 'lodash/debounce';
import * as atlasClient from '@/services/atlasClient';
import {
  ScreenContext,
  DataContext,
  SelectionContext,
  FilterContext,
  SettingsContext,
  ContextResponse,
  FullContextResponse,
  BatchContextUpdate,
  DataContextOptions,
} from '@/types/context';

/** Maximum items allowed in data arrays */
const MAX_DATA_ITEMS = 100;

/** Debounce delay in milliseconds for frequent updates */
const DEBOUNCE_DELAY = 300;

class ContextSyncService {
  /**
   * Debounced version of updateDataContext for frequent updates.
   * Use this when data changes rapidly (e.g., during filtering/sorting).
   */
  public updateDataContextDebounced: ReturnType<typeof debounce>;

  /**
   * Debounced version of updateSelectionContext for frequent updates.
   * Use this when selection changes rapidly (e.g., multi-select with checkboxes).
   */
  public updateSelectionContextDebounced: ReturnType<typeof debounce>;

  /**
   * Debounced version of updateFilterContext for frequent updates.
   * Use this when filters change rapidly (e.g., typing in search box).
   */
  public updateFilterContextDebounced: ReturnType<typeof debounce>;

  private currentSessionId: string | null = null;

  /**
   * Local cache of current context for synchronous access.
   * Updated whenever context is synced to backend.
   */
  private currentContext: {
    screen: ScreenContext | null;
    selection: SelectionContext | null;
    data: Record<string, DataContext>;
    filters: FilterContext | null;
    settings: Record<string, SettingsContext>;
  } = {
    screen: null,
    selection: null,
    data: {},
    filters: null,
    settings: {},
  };

  constructor() {
    // Initialize debounced methods
    this.updateDataContextDebounced = debounce(
      (dataType: string, data: Array<Record<string, any>>, options?: DataContextOptions) => {
        return this.updateDataContext(dataType, data, options);
      },
      DEBOUNCE_DELAY,
    );

    this.updateSelectionContextDebounced = debounce(
      (
        selectionType: string,
        selectedIds: Array<string | number>,
        selectedItems?: Array<Record<string, any>>,
      ) => {
        return this.updateSelectionContext(selectionType, selectedIds, selectedItems);
      },
      DEBOUNCE_DELAY,
    );

    this.updateFilterContextDebounced = debounce(
      (filterTarget: string, filters: Record<string, any>, searchQuery?: string) => {
        return this.updateFilterContext(filterTarget, filters, searchQuery);
      },
      DEBOUNCE_DELAY,
    );
  }

  // ===========================================================================
  // Session Management
  // ===========================================================================

  /**
   * Set the current session ID for context sync.
   * Call this when a chat session is created or resumed.
   */
  public setSessionId(sessionId: string): void {
    this.currentSessionId = sessionId;
  }

  /**
   * Get the current session ID.
   */
  public getSessionId(): string | null {
    return this.currentSessionId;
  }

  /**
   * Clear the current session ID and local context cache (synchronous, no backend call).
   *
   * Use this for immediate local cleanup when backend sync is not needed.
   * For full cleanup including backend, use `clearContext(true)` instead.
   */
  public clearSessionId(): void {
    this.currentSessionId = null;
    this.clearLocalCache();
  }

  /**
   * Get the current context synchronously from local cache.
   * Use this to attach context to chat messages without async API calls.
   *
   * @returns Deep clone of current cached context (screen, selection, data, filters, settings)
   */
  public getCurrentContext(): {
    screen: ScreenContext | null;
    selection: SelectionContext | null;
    data: Record<string, DataContext>;
    filters: FilterContext | null;
    settings: Record<string, SettingsContext>;
  } {
    try {
      // Prefer structuredClone for correct deep cloning with type preservation
      if (typeof structuredClone === 'function') {
        return structuredClone(this.currentContext);
      }
      // Fallback for environments without structuredClone
      return JSON.parse(JSON.stringify(this.currentContext));
    } catch {
      // If cloning fails, return shallow copy as last resort
      return { ...this.currentContext };
    }
  }

  // ===========================================================================
  // Screen Context
  // ===========================================================================

  /**
   * Update the current screen context.
   *
   * @param screen - Screen context to sync
   */
  public async updateScreenContext(screen: ScreenContext): Promise<ContextResponse | null> {
    // Cache locally first for synchronous access
    this.currentContext.screen = screen;

    try {
      const sessionId = this.ensureSessionId();

      return await atlasClient.post<ContextResponse>('/context/screen', {
        session_id: sessionId,
        screen,
      });
    } catch (error: any) {
      // Silent fail - context sync errors shouldn't block user actions
      return null;
    }
  }

  // ===========================================================================
  // Data Context
  // ===========================================================================

  /**
   * Update domain data context.
   *
   * @param dataType - Type identifier (e.g., 'reorder_suggestions', 'suppliers')
   * @param data - Array of data items (max 100)
   * @param options - Optional settings for the data context
   */
  public async updateDataContext(
    dataType: string,
    data: Array<Record<string, any>>,
    options: DataContextOptions = {},
  ): Promise<ContextResponse | null> {
    // Build data context object
    const dataContext: DataContext = {
      data_type: dataType,
      data: this.truncateData(data),
      total_count: options.totalCount ?? data.length,
      filters_applied: options.filtersApplied,
      sort_order: options.sortOrder,
      time_range: options.timeRange,
    };

    // Cache locally first for synchronous access
    this.currentContext.data[dataType] = dataContext;

    try {
      const sessionId = this.ensureSessionId();

      return await atlasClient.post<ContextResponse>('/context/data', {
        session_id: sessionId,
        data_type: dataType,
        data: dataContext,
        merge: options.merge ?? false,
      });
    } catch (error: any) {
      // Silent fail - context sync errors shouldn't block user actions
      return null;
    }
  }

  // ===========================================================================
  // Selection Context
  // ===========================================================================

  /**
   * Update selection context.
   *
   * @param selectionType - Type of selection (e.g., 'products', 'suppliers')
   * @param selectedIds - Array of selected item IDs
   * @param selectedItems - Optional full item data for selected items
   */
  public async updateSelectionContext(
    selectionType: string,
    selectedIds: Array<string | number>,
    selectedItems?: Array<Record<string, any>>,
  ): Promise<ContextResponse | null> {
    // Build selection context
    const selection: SelectionContext = {
      selection_type: selectionType,
      selected_ids: selectedIds,
      selected_items: selectedItems,
    };

    // Cache locally first for synchronous access
    this.currentContext.selection = selection;

    try {
      const sessionId = this.ensureSessionId();

      return await atlasClient.post<ContextResponse>('/context/selection', {
        session_id: sessionId,
        selection,
      });
    } catch (error: any) {
      // Silent fail - context sync errors shouldn't block user actions
      return null;
    }
  }

  // ===========================================================================
  // Filter Context
  // ===========================================================================

  /**
   * Update filter context.
   *
   * @param filterTarget - What the filters apply to (e.g., 'products', 'orders')
   * @param filters - Active filter key-value pairs
   * @param searchQuery - Optional active search query
   */
  public async updateFilterContext(
    filterTarget: string,
    filters: Record<string, any>,
    searchQuery?: string,
  ): Promise<ContextResponse | null> {
    // Build filter context
    const filterContext: FilterContext = {
      filter_target: filterTarget,
      filters,
      search_query: searchQuery,
    };

    // Cache locally first for synchronous access
    this.currentContext.filters = filterContext;

    try {
      const sessionId = this.ensureSessionId();

      return await atlasClient.post<ContextResponse>('/context/filters', {
        session_id: sessionId,
        filters: filterContext,
      });
    } catch (error: any) {
      // Silent fail - context sync errors shouldn't block user actions
      return null;
    }
  }

  /**
   * Set filter context locally without API call.
   * Filter will be sent with next chat message via metadata.
   * Use this when filters change frequently (e.g., dropdown selection).
   *
   * @param filterTarget - What the filters apply to (e.g., 'buying_table')
   * @param filters - Active filter key-value pairs
   * @param searchQuery - Optional active search query
   */
  public setLocalFilterContext(
    filterTarget: string,
    filters: Record<string, any>,
    searchQuery?: string,
  ): void {
    this.currentContext.filters = {
      filter_target: filterTarget,
      filters,
      search_query: searchQuery,
    };
  }

  /**
   * Clear local filter context.
   */
  public clearLocalFilterContext(): void {
    this.currentContext.filters = null;
  }

  // ===========================================================================
  // Settings Context
  // ===========================================================================

  /**
   * Update settings context.
   *
   * @param settingsType - Type of settings (e.g., 'store', 'forecasting')
   * @param settings - Settings key-value pairs
   * @param version - Optional version for change tracking
   */
  public async updateSettingsContext(
    settingsType: string,
    settings: Record<string, any>,
    version?: string,
  ): Promise<ContextResponse | null> {
    // Build settings context
    const settingsContext: SettingsContext = {
      settings_type: settingsType,
      settings,
      version,
    };

    // Cache locally first for synchronous access
    this.currentContext.settings[settingsType] = settingsContext;

    try {
      const sessionId = this.ensureSessionId();

      return await atlasClient.post<ContextResponse>('/context/settings', {
        session_id: sessionId,
        settings: settingsContext,
      });
    } catch (error: any) {
      // Silent fail - context sync errors shouldn't block user actions
      return null;
    }
  }

  // ===========================================================================
  // Batch Updates
  // ===========================================================================

  /**
   * Update multiple context types at once.
   * Use this to reduce network overhead when multiple contexts change together.
   *
   * @param updates - Object containing context updates
   */
  public async updateBatchContext(updates: BatchContextUpdate): Promise<ContextResponse | null> {
    // Process data once with truncation, used for both cache and payload
    const processedData = updates.data?.map((d) => ({
      ...d,
      data: this.truncateData(d.data),
    }));

    // Cache locally first for synchronous access
    if (updates.screen) {
      this.currentContext.screen = updates.screen;
    }
    if (processedData) {
      processedData.forEach((d) => {
        this.currentContext.data[d.data_type] = d;
      });
    }
    if (updates.selection) {
      this.currentContext.selection = updates.selection;
    }
    if (updates.filters) {
      this.currentContext.filters = updates.filters;
    }
    if (updates.settings) {
      updates.settings.forEach((s) => {
        this.currentContext.settings[s.settings_type] = s;
      });
    }

    try {
      const sessionId = this.ensureSessionId();

      // Build payload without mutating the input updates object
      const payload: Record<string, unknown> = {
        session_id: sessionId,
      };

      if (updates.screen) {
        payload.screen = updates.screen;
      }
      if (processedData) {
        payload.data = processedData;
      }
      if (updates.selection) {
        payload.selection = updates.selection;
      }
      if (updates.filters) {
        payload.filters = updates.filters;
      }
      if (updates.settings) {
        payload.settings = updates.settings;
      }

      return await atlasClient.post<ContextResponse>('/context/batch', payload);
    } catch {
      // Silent fail - context sync errors shouldn't block user actions
      return null;
    }
  }

  // ===========================================================================
  // Retrieval
  // ===========================================================================

  /**
   * Get full context for the current session.
   * Primarily for debugging/testing purposes.
   */
  public async getFullContext(): Promise<FullContextResponse | null> {
    try {
      const sessionId = this.ensureSessionId();

      return await atlasClient.get<FullContextResponse>(`/context/${sessionId}`);
    } catch (error: any) {
      // Silent fail - context sync errors shouldn't block user actions
      return null;
    }
  }

  // ===========================================================================
  // Cleanup
  // ===========================================================================

  /**
   * Clear all context for the current session.
   *
   * This clears the local cache and backend context but preserves the session ID
   * by default. Use `clearSession: true` when the session is truly ending (e.g.,
   * user logout, chat session close) to also clear the session ID.
   *
   * @param clearSession - If true, also clears the session ID after clearing context.
   *                       Use this when the session is ending, not just resetting context.
   * @returns ContextResponse on success, null if no session or API failure
   */
  public async clearContext(clearSession = false): Promise<ContextResponse | null> {
    // Clear local cache first (always succeeds)
    this.clearLocalCache();

    // Check for session ID without throwing - no session means nothing to clear on backend
    if (!this.currentSessionId) {
      // Local cache cleared, but no backend session to clear - this is valid
      return null;
    }

    let result: ContextResponse | null = null;
    try {
      result = await atlasClient.del<ContextResponse>(`/context/${this.currentSessionId}`);
    } catch {
      // Silent fail - context sync errors shouldn't block user actions
    }

    // Clear session ID after backend call if requested
    if (clearSession) {
      this.currentSessionId = null;
    }

    return result;
  }

  /**
   * Clear the local context cache.
   */
  private clearLocalCache(): void {
    this.currentContext = {
      screen: null,
      selection: null,
      data: {},
      filters: null,
      settings: {},
    };
  }

  // ===========================================================================
  // Private Helpers
  // ===========================================================================

  /**
   * Ensure session ID is set before making requests.
   */
  private ensureSessionId(): string {
    if (!this.currentSessionId) {
      throw new Error('ContextSyncService: Session ID not set. Call setSessionId() first.');
    }
    return this.currentSessionId;
  }

  /**
   * Truncate data array to max items limit.
   */
  private truncateData<T>(data: T[]): T[] {
    if (data.length > MAX_DATA_ITEMS) {
      return data.slice(0, MAX_DATA_ITEMS);
    }
    return data;
  }
}

// Export singleton instance
export const contextSyncService = new ContextSyncService();

// Export class for testing
export { ContextSyncService };
